import { useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import { formatCurrency } from '../utils/formatCurrency'
import {
  Home,
  Building,
  Building2,
  Store,
  Calendar
} from 'lucide-react'
import { VGVGeralVendas } from '../components/VGVGeralVendas'
import { ComissoesRecebidas } from '../components/ComissoesRecebidas'
import { TotalBonus } from '../components/TotalBonus'
import { useTiposImoveis } from '../hooks/useTiposImoveis'
import { useVendasPorTipo } from '../hooks/useVendasPorTipo'
import { useVendasPorMes } from '../hooks/useVendasPorMes'
import { useAuth } from '../contexts/AuthContext'
import { StatusAssinatura } from '../components/StatusAssinatura'
import { mesAtualKey, opcoesMesesFiltro, parseMesFiltro } from '../utils/filtroMes'

// Dados mockados para demonstração


// Cores para diferentes tipos de imóveis
const tipoImovelColors = {
  'apartamento': '#4f46e5',
  'casa': '#06b6d4',
  'terreno': '#10b981',
  'comercial': '#f59e0b',
  'rural': '#ef4444',
  'casa solta': '#8b5cf6',
  'teste will': '#ec4899',
  'outros': '#6b7280'
}

// Função para obter ícone baseado no tipo de imóvel
const getTipoImovelIcon = (tipo: string) => {
  switch (tipo.toLowerCase()) {
    case 'apartamento':
      return <Building className="w-4 h-4" />
    case 'casa':
      return <Home className="w-4 h-4" />
    case 'casa solta':
      return <Home className="w-4 h-4" />
    case 'terreno':
      return <Building2 className="w-4 h-4" />
    case 'comercial':
      return <Store className="w-4 h-4" />
    case 'rural':
      return <Building2 className="w-4 h-4" />
    case 'teste will':
      return <Building className="w-4 h-4" />
    default:
      return <Home className="w-4 h-4" />
  }
}



// Componente para o gráfico de vendas por mês
function VendasPorMesChart({ mesFiltro }: { mesFiltro: string }) {
  const { user } = useAuth()
  const { year } = parseMesFiltro(mesFiltro)
  const { data: vendasPorMesData, loading: vendasMesLoading, error: vendasMesError } = useVendasPorMes(
    user?.id,
    mesFiltro
  )

  if (vendasMesLoading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (vendasMesError) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 text-sm mb-2">Erro ao carregar dados</p>
          <p className="text-gray-500 dark:text-gray-400 text-xs">{vendasMesError}</p>
        </div>
      </div>
    )
  }

  if (!vendasPorMesData || vendasPorMesData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="text-center">
          <Home className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">Nenhuma venda registrada</p>
        </div>
      </div>
    )
  }

  const chartData = vendasPorMesData.map((item) => {
    const mesCompleto = item.mes.split(' ')[0]
    return {
      name: mesCompleto.slice(0, 3),
      mesLabel: mesCompleto,
      mesKey: item.mes_key,
      vendas: item.total_vendas,
      valor_total: item.valor_total,
      selecionado: item.mes_key === mesFiltro,
    }
  })

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <Tooltip
            cursor={false}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null
              const item = payload[0].payload as typeof chartData[number]
              return (
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md dark:border-gray-600 dark:bg-gray-800">
                  <p className="mb-2 text-sm font-semibold capitalize text-gray-900 dark:text-gray-100">
                    {item.mesLabel} {year}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    <span className="font-medium text-blue-600">Vendas:</span>{' '}
                    {item.vendas}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    <span className="font-medium text-green-600">VGV:</span>{' '}
                    {formatCurrency(item.valor_total)}
                  </p>
                </div>
              )
            }}
          />
          <Bar
            dataKey="vendas"
            radius={[4, 4, 0, 0]}
            maxBarSize={40}
            style={{
              cursor: 'pointer',
              outline: 'none',
              userSelect: 'none',
            }}
          >
            {chartData.map((entry) => (
              <Cell
                key={entry.mesKey}
                fill={entry.selecionado ? '#1d4ed8' : '#3b82f6'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function Dashboard() {
  const { user } = useAuth()
  const { loading: tiposLoading, error: tiposError } = useTiposImoveis()
  const { data: vendasPorTipoData, loading: vendasLoading, error: vendasError } = useVendasPorTipo(user?.id)
  const [mesFiltro, setMesFiltro] = useState(mesAtualKey)
  const opcoesMes = opcoesMesesFiltro(24)
  const isMesAtual = mesFiltro === mesAtualKey()

  // Preparar dados para o gráfico de pizza usando dados de vendas
  const tiposImoveisChartData = vendasPorTipoData.map((item) => ({
    name: item.tipo_nome,
    value: item.percentual,
    quantidade: item.total_vendas,
    valor_total: item.valor_total,
    valor_medio: item.valor_medio,
    color: tipoImovelColors[item.tipo_nome.toLowerCase() as keyof typeof tipoImovelColors] || '#6b7280'
  }))

  return (
    <div className="p-4 lg:p-6 dark:bg-gray-900">
      <StatusAssinatura ocultarQuandoAtiva />

      {/* Filtro por mês dos cards KPI */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Resumo do período</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Cards e gráfico de barras seguem o período selecionado
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
          <select
            value={mesFiltro}
            onChange={(e) => setMesFiltro(e.target.value)}
            className="min-w-[220px] rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Filtrar cards por mês"
          >
            {opcoesMes.map((opcao) => (
              <option key={opcao.value} value={opcao.value}>
                {opcao.label}
              </option>
            ))}
          </select>
          {isMesAtual && (
            <span className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300">
              Mês atual
            </span>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
        <VGVGeralVendas mesFiltro={mesFiltro} />
        <ComissoesRecebidas mesFiltro={mesFiltro} />
        <TotalBonus mesFiltro={mesFiltro} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 lg:mb-8">
        {/* Vendas por Mês Chart */}
        <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Vendas por Mês</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Ano {parseMesFiltro(mesFiltro).year}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-500 dark:text-gray-400">Vendas</span>
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline">
                Passe o mouse para ver o VGV
              </span>
            </div>
          </div>
          <VendasPorMesChart mesFiltro={mesFiltro} />
        </div>

        {/* Tipos de Imóveis Vendidos */}
        <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">Tipos de Imóveis Vendidos</h3>

          {(() => {
            if (tiposLoading || vendasLoading) {
              return (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              )
            } else if (tiposError || vendasError) {
              return (
                <div className="h-64 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-red-600 dark:text-red-400 text-sm mb-2">Erro ao carregar dados</p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">{tiposError || vendasError}</p>
                  </div>
                </div>
              )
            } else if (tiposImoveisChartData.length === 0) {
              return (
                <div className="h-64 flex items-center justify-center">
                  <div className="text-center">
                    <Home className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Nenhuma venda registrada</p>
                  </div>
                </div>
              )
            } else {
              return (
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Gráfico à esquerda */}
                  <div className="flex-1 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={tiposImoveisChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          strokeWidth={0}
                          style={{
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                            outline: 'none',
                            userSelect: 'none'
                          }}
                        >
                          {tiposImoveisChartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.color}
                              style={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                outline: 'none',
                                userSelect: 'none'
                              }}
                              onMouseEnter={(e) => {
                                const target = e.target as HTMLElement;
                                target.style.filter = 'brightness(1.1)';
                                target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                const target = e.target as HTMLElement;
                                target.style.filter = 'brightness(1)';
                                target.style.transform = 'scale(1)';
                              }}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            fontSize: '14px'
                          }}
                          formatter={(value, name, props) => [
                            `${value}% (${props.payload.quantidade} vendas)`,
                            name
                          ]}
                          labelFormatter={(label, payload) => {
                            if (payload && payload[0]) {
                              const data = payload[0].payload
                              return `${data.name} - ${formatCurrency(data.valor_total)}`
                            }
                            return label
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Dados à direita */}
                  <div className="flex-1 space-y-3">
                    {tiposImoveisChartData.map((tipo, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: tipo.color }}></div>
                            <div className="flex items-center">
                              {getTipoImovelIcon(tipo.name)}
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 ml-2 capitalize">{tipo.name}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{tipo.value}%</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">({tipo.quantidade} vendas)</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-xs text-gray-600 dark:text-gray-300">
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Valor Total:</span>
                            <br />
                            <span className="text-green-600 dark:text-green-400 font-semibold">{formatCurrency(tipo.valor_total)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Valor Médio:</span>
                            <br />
                            <span className="text-blue-600 dark:text-blue-400 font-semibold">{formatCurrency(tipo.valor_medio)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            }
          })()}
        </div>
      </div>

    </div>
  )
}